import {
  AttendanceEmployee,
  AttendanceVoce,
  AvailableVoce,
  getAvailableVoci,
  saveAttendance,
} from "@/api/openapi";
import { AttendancesContext, PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  addVoceToAttendance,
  attendanceToTempAttendance,
  removeVoceFromAttendance,
} from "../../attendanceUtils";

type UseAttendanceDesktopVociDrawerParams = {
  onClose: () => void;
  employee: AttendanceEmployee | null;
};

export function useAttendanceDesktopVociDrawer({
  onClose,
  employee,
}: UseAttendanceDesktopVociDrawerParams) {
  const { lang, selectedMandate } = useContext(PspContext);
  const {
    setAttendanceMonth,
    setTempAttendanceMonth,
    attendanceMonth,
    selectedMonth,
    selectedYear,
  } = useContext(AttendancesContext);

  const [availableVoci, setAvailableVoci] = useState<AvailableVoce[]>();
  // "" is used for not having selected anything yet (undefined doesn't work)
  const [selectedVoceId, setSelectedVoceId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [baseValue, setBaseValue] = useState("");
  const [removeVoceDialogOpen, setRemoveVoceDialogOpen] = useState(false);
  const [aboutToRemoveVoceIndex, setAboutToRemoveVoceIndex] = useState<
    number | null
  >(null);

  const selectedVoce = availableVoci?.find(
    (v) => v.id === Number.parseInt(selectedVoceId),
  );
  const voci = attendanceMonth?.attendances.find(
    (a) => a.employee.matricola === employee?.matricola,
  )?.voci;

  useEffect(() => {
    getAvailableVoci().then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        return;
      }
      setAvailableVoci(data);
    });
  }, [lang]);

  function resetState() {
    setSelectedVoceId("");
    setQuantity("");
    setBaseValue("");
  }

  function onCloseWrapper() {
    setTimeout(() => {
      resetState();
    }, 200);
    onClose();
  }

  function onRemoveVoce() {
    if (!employee || !attendanceMonth || aboutToRemoveVoceIndex === null) {
      toast(translate("somethingWentWrong", lang));
      return;
    }
    if (!selectedMandate) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    if (selectedMonth === undefined || selectedYear === undefined) {
      return;
    }

    const newAttendance = removeVoceFromAttendance(
      attendanceMonth,
      employee.matricola,
      employee.filiale,
      aboutToRemoveVoceIndex,
    );
    saveAttendance({
      query: { draft: true },
      body: {
        data: newAttendance.attendances,
        mandateId: selectedMandate.id,
        month: selectedMonth,
        year: selectedYear,
      },
    }).then(({ error, data }) => {
      if (error) {
        toast(translate("couldntSaveChanges", lang));
        return;
      }
      setAttendanceMonth(data);
      setTempAttendanceMonth(attendanceToTempAttendance(data));
      toast(translate("voceRemovedSuccessfully", lang));
      onCloseRemoveVoceDialog();
    });
  }

  function onAddVoce() {
    if (!employee || !attendanceMonth || !selectedVoce) {
      toast(translate("somethingWentWrong", lang));
      return;
    }
    if (!selectedMandate) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    if (selectedMonth === undefined || selectedYear === undefined) {
      return;
    }

    const selectedVoceIdNumber = Number.parseInt(selectedVoceId);
    if (Number.isNaN(selectedVoceIdNumber)) {
      toast(translate("somethingWentWrong", lang));
      return;
    }
    const baseValueNumber = Number.parseFloat(baseValue);
    if (Number.isNaN(baseValueNumber)) {
      if (selectedVoce.voceType === "base_by_quantity") {
        toast(translate("baseIsNotAValidNumber", lang));
      } else {
        toast(translate("amountIsNotAValidNumber", lang));
      }
      return;
    }
    const quantityNumber = Number.parseFloat(quantity);
    if (
      Number.isNaN(quantityNumber) &&
      selectedVoce.voceType === "base_by_quantity"
    ) {
      toast(translate("quantityIsNotAValidNumber", lang));
      return;
    }
    const newVoce: AttendanceVoce = {
      ...selectedVoce,
      base: baseValueNumber,
      quantity:
        selectedVoce.voceType === "base_by_quantity" ? quantityNumber : 1,
    };
    const newAttendance = addVoceToAttendance(
      attendanceMonth,
      employee.matricola,
      employee.filiale,
      newVoce,
    );
    saveAttendance({
      query: { draft: true },
      body: {
        data: newAttendance.attendances,
        mandateId: selectedMandate.id,
        month: selectedMonth,
        year: selectedYear,
      },
    }).then(({ error, data }) => {
      if (error) {
        toast(translate("couldntSaveChanges", lang));
        return;
      }
      setAttendanceMonth(data);
      setTempAttendanceMonth(attendanceToTempAttendance(data));
      toast(translate("voceAddedSuccessfully", lang));
      resetState();
    });
  }

  function onOpenRemoveVoceDialog(voceIndex: number) {
    setAboutToRemoveVoceIndex(voceIndex);
    setRemoveVoceDialogOpen(true);
  }

  function onCloseRemoveVoceDialog() {
    setRemoveVoceDialogOpen(false);
    setTimeout(() => {
      setAboutToRemoveVoceIndex(null);
    }, 250);
  }

  return {
    availableVoci,
    selectedVoceId,
    setSelectedVoceId,
    lang,
    selectedVoce,
    quantity,
    setQuantity,
    baseValue,
    setBaseValue,
    onCloseWrapper,
    onRemoveVoce,
    voci,
    onOpenRemoveVoceDialog,
    onCloseRemoveVoceDialog,
    onAddVoce,
    removeVoceDialogOpen,
    aboutToRemoveVoceIndex,
  };
}
